import { formatAuditAction as formatAction } from '@/lib/auditDisplay';
/**
 * EntityAuditTimeline - Displays audit history for a specific entity
 *
 * Features:
 * - Collapsible section for entity edit drawers
 * - Vertical timeline with action icons
 * - Expandable field changes (old → new values)
 * - Cursor-based "Load more" pagination
 * - Works for companies, groups, sites, units, and users
 */

import { useState, useEffect } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  AlertCircle,
  User,
  Edit,
  Plus,
  Trash2,
  Shield,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useEntityAuditLogs } from '@/hooks/useEntityAuditLogs';
import { cn } from '@/lib/utils';
import type { AuditLog } from '@/types';

export type AuditEntityType = 'company' | 'group' | 'site' | 'unit' | 'user';

interface EntityAuditTimelineProps {
  /** The type of entity */
  entityType: AuditEntityType;
  /** The entity ID */
  entityId: string;
  /** The entity name (for display) */
  entityName?: string;
  /** Number of items per page */
  pageSize?: number;
  /** Whether the section is initially expanded */
  defaultExpanded?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Get action icon based on action type
function getActionIcon(action: string) {
  const iconClass = 'w-3.5 h-3.5';
  const lowerAction = action?.toLowerCase() || '';

  if (lowerAction.includes('login') || lowerAction.includes('logout')) {
    return <User className={iconClass} />;
  }
  if (lowerAction.includes('delete') || lowerAction.includes('remove')) {
    return <Trash2 className={iconClass} />;
  }
  if (lowerAction.includes('create') || lowerAction.includes('invite') || lowerAction.includes('add')) {
    return <Plus className={iconClass} />;
  }
  if (lowerAction.includes('update') || lowerAction.includes('edit') || lowerAction.includes('modify')) {
    return <Edit className={iconClass} />;
  }
  if (lowerAction.includes('system') || lowerAction.includes('sync')) {
    return <Shield className={iconClass} />;
  }
  return <Clock className={iconClass} />;
}

// Get action color based on action type
function getActionColor(action: string, severity: string) {
  if (severity === 'critical') return 'text-red-400 bg-red-500/20';
  if (severity === 'warning') return 'text-yellow-400 bg-yellow-500/20';

  const lowerAction = action?.toLowerCase() || '';
  if (lowerAction.includes('delete') || lowerAction.includes('remove')) {
    return 'text-red-400 bg-red-500/20';
  }
  if (lowerAction.includes('create') || lowerAction.includes('add')) {
    return 'text-green-400 bg-green-500/20';
  }
  return 'text-blue-400 bg-blue-500/20';
}


// Format field name for display
function formatFieldName(field: string) {
  if (!field) return '';
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();
}

// Format value for display
function formatValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  // Try to format as date if it looks like ISO date
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return format(date, 'MMM d, yyyy');
      }
    } catch {
      // Fall through
    }
  }
  return value;
}

// Single audit log entry component
function AuditLogEntry({
  log,
  isExpanded,
  onToggle,
  loadDetails,
}: {
  log: AuditLog;
  isExpanded: boolean;
  onToggle: () => void;
  loadDetails: () => Promise<void>;
}) {
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const hasDetails = log.hasDetails;
  const details = log.subItems;

  const handleToggle = async () => {
    if (hasDetails && !details && !isLoadingDetails) {
      setIsLoadingDetails(true);
      await loadDetails();
      setIsLoadingDetails(false);
    }
    onToggle();
  };

  const fieldChanges = details?.filter(d => d.type === 'field_change') || [];

  return (
    <div className="relative pl-6 pb-4 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[9px] top-5 bottom-0 w-px bg-border last:hidden" />

      {/* Timeline dot with icon */}
      <div
        className={cn(
          'absolute left-0 top-1 w-[18px] h-[18px] rounded-full flex items-center justify-center',
          getActionColor(log.action, log.severity)
        )}
      >
        {getActionIcon(log.action)}
      </div>

      {/* Content */}
      <div className="min-w-0">
        <div
          className={cn(
            'flex items-start gap-2 cursor-pointer group',
            hasDetails && 'hover:bg-muted/50 -mx-2 px-2 py-1 rounded-md'
          )}
          onClick={hasDetails ? handleToggle : undefined}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">
                {formatAction(log.action)}
              </span>
              {log.severity !== 'info' && (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[10px] px-1.5 py-0',
                    log.severity === 'critical'
                      ? 'border-red-500/30 text-red-400'
                      : 'border-yellow-500/30 text-yellow-400'
                  )}
                >
                  {log.severity}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {log.details}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <span title={log.user}>{log.name || log.user?.split('@')[0]}</span>
              <span>·</span>
              <span title={format(new Date(log.timestamp), 'PPpp')}>
                {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
              </span>
            </div>
          </div>

          {hasDetails && (
            <div className="flex-shrink-0 pt-1">
              {isLoadingDetails ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              ) : (
                <ChevronRight
                  className={cn(
                    'w-3.5 h-3.5 text-muted-foreground transition-transform',
                    isExpanded && 'rotate-90'
                  )}
                />
              )}
            </div>
          )}
        </div>

        {/* Expanded details */}
        {isExpanded && hasDetails && details && fieldChanges.length > 0 && (
          <div className="mt-2 ml-2 space-y-1 border-l-2 border-muted pl-3">
            {fieldChanges.map((change, idx) => (
              <div key={idx} className="text-xs">
                <span className="text-muted-foreground">
                  {formatFieldName(change.fieldName || '')}:
                </span>
                <span className="text-foreground/60 ml-1">
                  {formatValue(change.oldValue)}
                </span>
                <ArrowRight className="w-3 h-3 inline mx-1 text-muted-foreground" />
                <span className="text-foreground ml-1">
                  {formatValue(change.newValue)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function EntityAuditTimeline({
  entityType,
  entityId,
  entityName: _entityName,
  pageSize = 10,
  defaultExpanded = false,
  className,
}: EntityAuditTimelineProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const {
    logs,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    refresh,
    expandedLogIds,
    toggleExpanded,
    loadLogDetails,
  } = useEntityAuditLogs({
    entityType,
    entityId,
    pageSize,
    autoFetch: isExpanded, // Only fetch when expanded
  });

  // Track if initial fetch has completed to prevent infinite loops
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch when expanded for the first time (not when empty results return)
  useEffect(() => {
    if (isExpanded && !hasFetched && !isLoading) {
      setHasFetched(true);
      refresh();
    }
  }, [isExpanded, hasFetched, isLoading, refresh]);

  // Reset hasFetched when entityId changes
  useEffect(() => {
    setHasFetched(false);
  }, [entityId]);

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn('border border-border rounded-lg', className)}
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between p-3 h-auto hover:bg-muted/50"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Activity History</span>
            {total > 0 && (
              <Badge variant="secondary" className="text-xs">
                {total}
              </Badge>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 pb-3">
          {/* Loading state */}
          {isLoading && logs.length === 0 && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 py-4">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7"
                onClick={() => refresh()}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && logs.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">
              No activity recorded yet
            </div>
          )}

          {/* Timeline */}
          {logs.length > 0 && (
            <div className="pt-2">
              {logs.map(log => (
                <AuditLogEntry
                  key={log.id}
                  log={log}
                  isExpanded={expandedLogIds.has(log.id)}
                  onToggle={() => toggleExpanded(log.id)}
                  loadDetails={async () => {
                    await loadLogDetails(log.id);
                  }}
                />
              ))}

              {/* Load more button */}
              {hasMore && (
                <div className="pt-2 pl-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => loadMore()}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>Load more</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
